import {
  MessageSquare, Shield, Users, Target,
  Volume2, Brain, LineChart
} from "lucide-react";

export function ProblemSolution() {
  return (
    <>
      {/* PROBLEM */}
      <section className="py-14 sm:py-20 px-5 sm:px-6 lg:px-8 bg-[#0B1F3B]" data-testid="problem-section">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-xs sm:text-sm font-semibold text-[#14B8A6] uppercase tracking-widest mb-3 sm:mb-4">The Problem</h2>
          <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4 sm:mb-6">Why Sales Conversations Fail</h3>
          <p className="text-slate-400 text-sm sm:text-base max-w-2xl mx-auto mb-10 sm:mb-14 px-2">Most advisors jump into client meetings underprepared. The result? Lost deals, missed objections, and poor customer trust.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {[
              { icon: MessageSquare, title: "Jumping to Product", desc: "Pitching features before understanding needs" },
              { icon: Shield, title: "Poor Objection Handling", desc: "Freezing when customers push back on price or trust" },
              { icon: Users, title: "No Practice Environment", desc: "First real practice happens with a real customer" },
              { icon: Target, title: "Generic Training", desc: "Classroom role plays don't reflect real Indian customer behavior" },
            ].map(({ icon: Icon, title, desc }) => (
              <div key={title} className="p-5 sm:p-6 bg-white/5 backdrop-blur-sm rounded-xl sm:rounded-2xl border border-white/10 text-left">
                <div className="w-9 h-9 sm:w-10 sm:h-10 bg-red-500/20 rounded-lg sm:rounded-xl flex items-center justify-center mb-3 sm:mb-4">
                  <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-red-400" />
                </div>
                <h4 className="text-white font-semibold text-sm sm:text-base mb-1.5 sm:mb-2">{title}</h4>
                <p className="text-slate-400 text-xs sm:text-sm leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SOLUTION */}
      <section className="py-14 sm:py-20 px-5 sm:px-6 lg:px-8" data-testid="solution-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 sm:mb-14">
            <h2 className="text-xs sm:text-sm font-semibold text-[#2563EB] uppercase tracking-widest mb-3 sm:mb-4">The Solution</h2>
            <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0B1F3B] mb-3 sm:mb-4">AI Voice Role Play That Feels Real</h3>
            <p className="text-slate-500 text-sm sm:text-base max-w-2xl mx-auto px-2">Practice with AI customers that hesitate, resist, and challenge — just like real prospects. Get coaching that's sharp, specific, and actionable.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5 sm:gap-8">
            {[
              { icon: Volume2, color: "#14B8A6", title: "Realistic Voice Conversations", desc: "AI personas with distinct voices speak in English, Hindi, or Hinglish. They interrupt, push back, and test your skills." },
              { icon: Brain, color: "#2563EB", title: "Coach-Level Feedback", desc: "Every session is evaluated with a Readiness Score, missed opportunities, and specific coaching on what a top performer would say." },
              { icon: LineChart, color: "#F59E0B", title: "Measurable Readiness", desc: "Track competency growth across Need Analysis, Objection Handling, Closing, and more. Know exactly who's field-ready." },
            ].map(({ icon: Icon, color, title, desc }) => (
              <div key={title} className="group p-6 sm:p-8 rounded-xl sm:rounded-2xl border border-slate-200/60 hover:border-transparent hover:shadow-xl transition-all duration-300">
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl flex items-center justify-center mb-4 sm:mb-5" style={{ backgroundColor: `${color}15` }}>
                  <Icon className="w-6 h-6 sm:w-7 sm:h-7" style={{ color }} />
                </div>
                <h4 className="text-base sm:text-lg font-bold text-[#0B1F3B] mb-2 sm:mb-3">{title}</h4>
                <p className="text-xs sm:text-sm text-slate-500 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
